import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import { Money } from "@/components/wallet/shared";
import { useWalletItems } from "@/components/wallet/ItemsDialog";

const EMPTY = { owner: null, item_id: "", quantity: "1", amount: "", note: "" };

function OwnerSearch({ value, onChange }) {
  const [q, setQ] = useState("");
  const fetchOwners = (t) => async () => (await api.get("/wallet/owners", { params: { owner_type: t } })).data;
  const { data: st = [] } = useQuery({ queryKey: ["wallet-owners", "student"], queryFn: fetchOwners("student") });
  const { data: te = [] } = useQuery({ queryKey: ["wallet-owners", "teacher"], queryFn: fetchOwners("teacher") });
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return [...st, ...te].filter((o) => `${o.name} ${o.matricola || ""} ${o.email || ""}`.toLowerCase().includes(s)).slice(0, 8);
  }, [q, st, te]);

  if (value) {
    return (
      <div className="mt-1.5 flex items-center gap-2 p-2 rounded-lg border border-[#0F766E]/30 bg-[#0F766E]/5" data-testid="charge-owner-selected">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{value.name}</div>
          <div className="text-[11px] text-stone-500">{value.owner_type === "teacher" ? "Docente" : "Studente"} · saldo <Money value={value.balance}/></div>
        </div>
        <button type="button" onClick={() => onChange(null)} className="text-stone-400 hover:text-stone-700" data-testid="charge-owner-clear"><X size={14}/></button>
      </div>
    );
  }
  return (
    <div className="relative mt-1.5">
      <Search size={14} className="absolute left-3 top-3 text-stone-400"/>
      <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca studente o docente" className="pl-8" data-testid="charge-owner-search"/>
      {results.length > 0 && (
        <ul className="absolute z-10 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.map((o) => (
            <li key={`${o.owner_type}-${o.owner_id}`}>
              <button type="button" onClick={() => onChange(o)} className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50 flex justify-between gap-2" data-testid={`charge-owner-opt-${o.owner_id}`}>
                <span className="truncate">{o.name} <span className="text-[10px] text-stone-500 uppercase">{o.owner_type === "teacher" ? "Docente" : o.matricola}</span></span>
                <Money value={o.balance} className="text-xs"/>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ChargeDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const { data: items = [] } = useWalletItems();
  const { data: detail } = useQuery({
    queryKey: ["wallet-owner", f.owner?.owner_type, f.owner?.owner_id], enabled: !!f.owner,
    queryFn: async () => (await api.get(`/wallet/owners/${f.owner.owner_type}/${f.owner.owner_id}`)).data,
  });
  const unitsLeft = (detail?.units || []).find((u) => u.item_id === f.item_id)?.remaining || 0;
  const usedUnits = f.item_id ? Math.min(unitsLeft, parseFloat(f.quantity) || 0) : 0;
  const selItem = items.find((i) => i.id === f.item_id);
  const effective = usedUnits && selItem ? selItem.price * ((parseFloat(f.quantity) || 0) - usedUnits) : parseFloat(f.amount) || 0;
  const active = items.filter((i) => i.active);
  const pickItem = (it) => {
    if (f.item_id === it.id) return setF({ ...f, item_id: "", amount: "" });
    const qty = parseFloat(f.quantity) || 1;
    setF({ ...f, item_id: it.id, amount: (it.price * qty).toFixed(2) });
  };
  const setQty = (v) => {
    const it = items.find((i) => i.id === f.item_id);
    const qty = parseFloat(v) || 0;
    setF({ ...f, quantity: v, amount: it ? (it.price * qty).toFixed(2) : f.amount });
  };
  const close = () => { setF(EMPTY); onOpenChange(false); };
  const mut = useMutation({
    mutationFn: async () => (await api.post("/wallet/transactions", {
      owner_type: f.owner.owner_type, owner_id: f.owner.owner_id, kind: "addebito", amount: parseFloat(f.amount),
      item_id: f.item_id || null, quantity: f.item_id ? parseFloat(f.quantity) || 1 : null, description: f.note || null,
    })).data,
    onSuccess: () => {
      toast.success(`Addebito registrato a ${f.owner.name}`);
      ["wallet-owners", "wallet-summary", "wallet-owner"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      close();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const valid = f.owner && parseFloat(f.amount) > 0 && (f.item_id || f.note.trim());
  const after = f.owner ? (detail?.balance ?? f.owner.balance ?? 0) - effective : null;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="charge-dialog">
        <DialogHeader>
          <DialogTitle>Addebita acquisto</DialogTitle>
          <DialogDescription>L'importo viene scalato dal wallet dello studente o del docente.</DialogDescription>
        </DialogHeader>
        <div><Label className="text-xs">Studente o docente</Label><OwnerSearch value={f.owner} onChange={(o) => setF({ ...f, owner: o })}/></div>
        <div>
          <Label className="text-xs">Voce di spesa</Label>
          {active.length === 0 ? (
            <div className="text-xs text-stone-500 mt-1.5">Nessuna voce nel listino: crea le voci da "Listino" oppure scrivi una descrizione libera qui sotto.</div>
          ) : (
            <div className="flex gap-1.5 flex-wrap mt-1.5">
              {active.map((it) => (
                <button key={it.id} type="button" onClick={() => pickItem(it)} data-testid={`charge-item-${it.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.item_id === it.id ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white border-stone-200 hover:border-[#0F766E]"}`}>
                  {it.name} · {it.price.toFixed(2)} €
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Quantità</Label><Input type="number" min="1" step="1" value={f.quantity} disabled={!f.item_id} onChange={(e) => setQty(e.target.value)} className="mt-1.5" data-testid="charge-qty"/></div>
          <div><Label className="text-xs">Importo totale (€)</Label><Input type="number" min="0.01" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1.5" data-testid="charge-amount"/></div>
        </div>
        <div><Label className="text-xs">{f.item_id ? "Nota (facoltativa)" : "Descrizione"}</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Es. caffè distributore" className="mt-1.5" data-testid="charge-note"/></div>
        {usedUnits > 0 && (
          <div className="text-xs p-2 rounded-lg bg-[#0F766E]/10 text-[#0F766E]" data-testid="charge-units-info">
            Verranno usati {usedUnits} {selItem?.name} dal pacchetto prepagato (ne restano {unitsLeft}). Da pagare: € {effective.toFixed(2)}
          </div>
        )}
        {after !== null && (
          <div className="text-xs text-stone-600" data-testid="charge-balance-after">Saldo dopo l'addebito: <Money value={after} className="font-semibold"/></div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close}>Annulla</Button>
          <Button className="bg-[#0F766E] hover:bg-[#115E59]" disabled={!valid || mut.isPending} onClick={() => mut.mutate()} data-testid="charge-submit">
            {mut.isPending ? "Salvataggio…" : "Addebita"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
